import { CoreError, type SyncOp } from '@ims/core'
import {
  SyncError,
  encodeRow,
  isTableName,
  rowSchema,
  tablesByName,
  validateOps,
  type CommitResult,
  type Entitlements,
  type FailedTx,
  type PushTx,
  type SyncController,
  type SyncState,
  type SyncTransport,
  type TableName,
} from '@ims/data'
import { getCursor, getMeta, opStatements, placeholders, setMetaSql } from './db.ts'
import type { SqlDriver, SqlStatement } from './driver.ts'

export interface SyncEngineOptions {
  driver: SqlDriver
  transport: SyncTransport
  tenantId: string
  installationId: string
  now?: () => string
  newId?: () => string
  /** Txs per sync_push call. */
  pushBatchSize?: number
  /**
   * Ops per sync_push call (always at least one tx). Keeps one RPC well inside the
   * server's statement timeout when big imports queue thousands of ops.
   */
  maxOpsPerPush?: number
  pullPageSize?: number
  /** Periodic sync while started (ms). */
  pollIntervalMs?: number
  /** Delay before pushing after a local commit (ms). */
  commitDebounceMs?: number
  setTimer?: (fn: () => void, ms: number) => unknown
  clearTimer?: (handle: unknown) => void
}

type TablesListener = (tables: TableName[]) => void

const MAX_BACKOFF_MS = 60_000

/** A device that has not checked in for this long stops money and stock work. */
export const STALE_AFTER_MS = 3 * 24 * 60 * 60 * 1000

/**
 * A clock this far behind the last check-in counts as moved back. Clocks are
 * corrected by small amounts all the time; only a real jump back should lock.
 */
export const CLOCK_BACK_TOLERANCE_MS = 10 * 60 * 1000

/**
 * Ops a device may not write once it is stale (docs/plans/offline-safety.md):
 * bills, payments, credit notes and anything that moves stock. Drafts,
 * quotations, orders and kitchen tickets still work.
 */
export function blockedWhenStale(op: SyncOp): boolean {
  switch (op.table) {
    case 'sales_invoices':
    case 'credit_notes':
      return op.row.status !== 'draft'
    case 'payments':
    case 'payment_allocations':
    case 'stock_movements':
    case 'goods_receipts':
    case 'goods_receipt_lines':
      return true
    default:
      return false
  }
}

interface Checkin {
  /** The server's time at the check-in. */
  server_now: string
  /** This device's time at the check-in. */
  local_now: string
}

/**
 * Local-first write path and sync loop for one tenant:
 *   commit()  -> local SQLite + outbox, atomically (works offline)
 *   sync()    -> push outbox via sync_push, then pull via sync_pull
 * Runs are serialised; a trigger during a run schedules one more run.
 */
export class SyncEngine implements SyncController {
  private readonly driver: SqlDriver
  private readonly transport: SyncTransport
  private readonly tenantId: string
  private readonly installationId: string
  private readonly now: () => string
  private readonly newId: () => string
  private readonly pushBatchSize: number
  private readonly maxOpsPerPush: number
  private readonly pullPageSize: number
  private readonly pollIntervalMs: number
  private readonly commitDebounceMs: number
  private readonly setTimer: (fn: () => void, ms: number) => unknown
  private readonly clearTimer: (handle: unknown) => void

  private current: SyncState = {
    phase: 'idle',
    pending: 0,
    failed: [],
    lastPulledAt: null,
    lastError: null,
    oldestUnsyncedAt: null,
    lastCheckinAt: null,
  }
  private readonly stateListeners = new Set<(state: SyncState) => void>()
  private readonly tableListeners = new Set<TablesListener>()
  private running: Promise<void> | null = null
  private rerun = false
  private started = false
  private timer: unknown = null
  private backoffMs = 0
  private unsubscribeRealtime: (() => void) | null = null

  constructor(options: SyncEngineOptions) {
    this.driver = options.driver
    this.transport = options.transport
    this.tenantId = options.tenantId
    this.installationId = options.installationId
    this.now = options.now ?? (() => new Date().toISOString())
    this.newId = options.newId ?? (() => crypto.randomUUID())
    this.pushBatchSize = options.pushBatchSize ?? 50
    this.maxOpsPerPush = options.maxOpsPerPush ?? 1000
    this.pullPageSize = options.pullPageSize ?? 500
    this.pollIntervalMs = options.pollIntervalMs ?? 60_000
    this.commitDebounceMs = options.commitDebounceMs ?? 300
    this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms))
    this.clearTimer =
      options.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>))
  }

  // ---------------------------------------------------------------------------
  // State & listeners
  // ---------------------------------------------------------------------------

  state(): SyncState {
    return this.current
  }

  subscribe(listener: (state: SyncState) => void): () => void {
    this.stateListeners.add(listener)
    return () => this.stateListeners.delete(listener)
  }

  onTablesChanged(listener: TablesListener): () => void {
    this.tableListeners.add(listener)
    return () => this.tableListeners.delete(listener)
  }

  private setState(patch: Partial<SyncState>): void {
    this.current = { ...this.current, ...patch }
    for (const l of this.stateListeners) l(this.current)
  }

  private emitTables(tables: Iterable<string>): void {
    const names = [...new Set(tables)].filter(isTableName)
    if (names.length === 0) return
    for (const l of this.tableListeners) l(names)
  }

  /** Reloads pending/failed counts and the last pull time from the database. */
  async refreshState(): Promise<void> {
    const [counts, oldest, failedRows, lastPulledAt, checkin] = await Promise.all([
      this.driver.all<{ n: number }>(`SELECT COUNT(*) AS n FROM _outbox WHERE status = 'pending'`),
      this.driver.all<{ at: string | null }>(`SELECT MIN(created_at) AS at FROM _outbox`),
      this.driver.all<{
        tx_id: string
        last_code: string
        last_error: string
        failed_at: string
        tables: string
      }>(
        `SELECT tx_id, last_code, last_error, failed_at, tables FROM _outbox WHERE status = 'failed' ORDER BY seq`,
      ),
      getMeta(this.driver, 'last_pulled_at'),
      this.lastCheckin(),
    ])
    const failed: FailedTx[] = failedRows.map((r) => ({
      txId: r.tx_id,
      code: r.last_code,
      message: r.last_error,
      failedAt: r.failed_at,
      tables: JSON.parse(r.tables) as string[],
    }))
    this.setState({
      pending: counts[0]?.n ?? 0,
      failed,
      lastPulledAt,
      oldestUnsyncedAt: oldest[0]?.at ?? null,
      lastCheckinAt: checkin?.local_now ?? null,
    })
  }

  // ---------------------------------------------------------------------------
  // Local writes
  // ---------------------------------------------------------------------------

  /** Applies one user action locally and queues it for sync_push. */
  async commit(ops: SyncOp[]): Promise<CommitResult> {
    const stamped = await this.prepare(ops)
    return this.write(stamped, [])
  }

  /** Stamps the tenant on every row and runs the local checks a commit needs. */
  private async prepare(ops: SyncOp[]): Promise<SyncOp[]> {
    if (ops.length === 0) throw new CoreError('invalid_input', 'nothing to commit')
    // Every row is stamped with this tenant, whatever the caller passed.
    const stamped: SyncOp[] = ops.map((op) => ({
      ...op,
      row: { ...op.row, tenant_id: this.tenantId },
    }))
    const issues = validateOps(stamped)
    if (issues.length > 0) {
      throw new CoreError(
        'invalid_row',
        issues.map((i) => `${i.table}[${i.index}] ${i.message}`).join('; '),
      )
    }
    await this.assertCanWrite()
    await this.assertFresh(stamped)
    return stamped
  }

  /** Writes stamped, validated ops plus extra statements in one batch. */
  private async write(stamped: SyncOp[], before: SqlStatement[]): Promise<CommitResult> {
    const txId = this.newId()
    const now = this.now()
    const tables = [...new Set(stamped.map((o) => o.table))]
    const statements: SqlStatement[] = [
      ...before,
      ...stamped.flatMap((op) => opStatements(op, now)),
    ]
    statements.push({
      sql: 'INSERT INTO _outbox (tx_id, ops, tables, created_at) VALUES (?, ?, ?, ?)',
      params: [txId, JSON.stringify(stamped), JSON.stringify(tables), now],
    })
    for (const op of stamped) {
      statements.push({
        sql: 'INSERT OR IGNORE INTO _pending_rows (tx_id, tbl, row_id) VALUES (?, ?, ?)',
        params: [txId, op.table, String(op.row.id)],
      })
    }
    try {
      await this.driver.batch(statements)
    } catch (error) {
      if (/UNIQUE constraint failed/i.test(String(error))) {
        throw new CoreError('conflict', 'a document with this number already exists on this device')
      }
      throw error
    }
    this.setState({
      pending: this.current.pending + 1,
      oldestUnsyncedAt: this.current.oldestUnsyncedAt ?? now,
    })
    this.emitTables(tables)
    if (this.started) this.schedule(this.commitDebounceMs)
    return { txId, status: 'queued' }
  }

  /** The last entitlement snapshot from the server (null before the first sync). */
  async cachedEntitlements(): Promise<Entitlements | null> {
    const raw = await getMeta(this.driver, 'entitlements')
    return raw ? (JSON.parse(raw) as Entitlements) : null
  }

  private async lastCheckin(): Promise<Checkin | null> {
    const raw = await getMeta(this.driver, 'last_checkin')
    return raw ? (JSON.parse(raw) as Checkin) : null
  }

  /**
   * Money and stock need a recent view of the server: a device that has not
   * checked in for 3 days (or whose clock went back past its last check-in)
   * is stale. Never checked in yet: the server still decides.
   */
  async isStale(): Promise<boolean> {
    const checkin = await this.lastCheckin()
    if (!checkin) return false
    const now = Date.parse(this.now())
    const at = Date.parse(checkin.local_now)
    return at - now > CLOCK_BACK_TOLERANCE_MS || now - at > STALE_AFTER_MS
  }

  private async assertFresh(ops: SyncOp[]): Promise<void> {
    if (!ops.some(blockedWhenStale)) return
    if (await this.isStale()) {
      throw new CoreError(
        'device_stale',
        'this device has not synced for more than 3 days. Connect to the internet to bill, take payments or change stock',
      )
    }
  }

  private async assertCanWrite(): Promise<void> {
    const raw = await getMeta(this.driver, 'entitlements')
    if (!raw) return // never synced yet: the server still decides
    const ent = JSON.parse(raw) as Entitlements
    const graceOver = ent.grace_until !== null && this.now() > ent.grace_until
    if (!ent.can_write || graceOver) {
      throw new CoreError(
        'subscription_inactive',
        'the subscription is inactive or its offline grace period has ended',
      )
    }
  }

  // ---------------------------------------------------------------------------
  // Sync
  // ---------------------------------------------------------------------------

  /** Push, then pull. Serialised: concurrent calls share (or queue) a run. */
  syncNow(): Promise<void> {
    if (this.running) {
      this.rerun = true
      return this.running
    }
    this.running = (async () => {
      try {
        do {
          this.rerun = false
          await this.runOnce()
        } while (this.rerun)
      } finally {
        this.running = null
      }
    })()
    return this.running
  }

  private async runOnce(): Promise<void> {
    try {
      this.setState({ phase: 'pushing', lastError: null })
      await this.push()
      this.setState({ phase: 'pulling' })
      await this.pull()
      await this.refreshEntitlements()
      this.backoffMs = 0
      this.setState({ phase: 'idle' })
    } catch (error) {
      const retryable = error instanceof SyncError ? error.retryable : false
      const message = error instanceof Error ? error.message : String(error)
      this.backoffMs = retryable ? Math.min(Math.max(this.backoffMs * 2, 2_000), MAX_BACKOFF_MS) : 0
      this.setState({ phase: retryable ? 'offline' : 'error', lastError: message })
      if (retryable && this.started) this.schedule(this.backoffMs)
    } finally {
      await this.refreshState()
    }
  }

  private async push(): Promise<void> {
    for (;;) {
      const rows = await this.driver.all<{
        seq: number
        tx_id: string
        ops: string
        tables: string
      }>(
        `SELECT seq, tx_id, ops, tables FROM _outbox WHERE status = 'pending' ORDER BY seq LIMIT ?`,
        [this.pushBatchSize],
      )
      if (rows.length === 0) return
      const txs: PushTx[] = []
      let opCount = 0
      for (const r of rows) {
        const ops = JSON.parse(r.ops) as SyncOp[]
        if (txs.length > 0 && opCount + ops.length > this.maxOpsPerPush) break
        txs.push({ tx_id: r.tx_id, ops })
        opCount += ops.length
      }
      await this.driver.batch([
        {
          sql: `UPDATE _outbox SET attempts = attempts + 1 WHERE tx_id IN (${placeholders(txs.map((t) => t.tx_id))})`,
          params: txs.map((t) => t.tx_id),
        },
      ])

      const results = await this.transport.push(this.tenantId, this.installationId, txs)
      const byId = new Map(results.map((r) => [r.tx_id, r]))
      const statements: SqlStatement[] = []
      let settled = 0
      for (const tx of txs) {
        const result = byId.get(tx.tx_id)
        if (!result) continue // not processed: stays pending
        settled++
        if (result.status === 'rejected') {
          statements.push({
            sql: `UPDATE _outbox SET status = 'failed', last_code = ?, last_error = ?, failed_at = ? WHERE tx_id = ?`,
            params: [
              result.code ?? 'error',
              result.message ?? 'rejected by the server',
              this.now(),
              tx.tx_id,
            ],
          })
        } else {
          statements.push({ sql: 'DELETE FROM _outbox WHERE tx_id = ?', params: [tx.tx_id] })
          statements.push({ sql: 'DELETE FROM _pending_rows WHERE tx_id = ?', params: [tx.tx_id] })
        }
      }
      if (statements.length > 0) await this.driver.batch(statements)
      if (settled < txs.length) {
        throw new SyncError('incomplete_push', 'the server did not process every transaction', true)
      }
    }
  }

  private async pull(): Promise<void> {
    let cursor = await getCursor(this.driver)
    for (;;) {
      const page = await this.transport.pull(this.tenantId, cursor, this.pullPageSize)
      const statements: SqlStatement[] = []
      const touched = new Set<string>()
      for (const change of page.changes) {
        if (!isTableName(change.table)) continue // table added by a newer server
        const parsed = rowSchema(change.table).safeParse(change.row)
        if (!parsed.success) {
          throw new SyncError(
            'invalid_pull',
            `bad ${change.table} row: ${parsed.error.message}`,
            false,
          )
        }
        statements.push(pullUpsert(change.table, parsed.data))
        touched.add(change.table)
      }
      statements.push(setMetaSql('pull_cursor', JSON.stringify(page.cursor)))
      if (!page.has_more) statements.push(setMetaSql('last_pulled_at', this.now()))
      await this.driver.batch(statements)
      this.emitTables(touched)
      cursor = page.cursor
      if (!page.has_more) return
    }
  }

  private async refreshEntitlements(): Promise<void> {
    const ent = await this.transport.entitlements(this.tenantId, this.installationId)
    const checkin: Checkin = { server_now: ent.server_now ?? ent.issued_at, local_now: this.now() }
    await this.driver.batch([
      setMetaSql('entitlements', JSON.stringify(ent)),
      setMetaSql('last_checkin', JSON.stringify(checkin)),
    ])
  }

  // ---------------------------------------------------------------------------
  // Failed txs
  // ---------------------------------------------------------------------------

  /** Queues a rejected tx again (e.g. after the subscription was renewed). */
  async retry(txId: string): Promise<void> {
    await this.driver.batch([
      {
        sql: `UPDATE _outbox SET status = 'pending', attempts = 0, last_code = NULL, last_error = NULL, failed_at = NULL WHERE tx_id = ? AND status = 'failed'`,
        params: [txId],
      },
    ])
    await this.refreshState()
    if (this.started) this.schedule(0)
  }

  /**
   * Drops a rejected tx and restores its rows to the server's version (or
   * removes them if the server never had them).
   */
  async discard(txId: string): Promise<void> {
    const undo = await this.discardStatements(txId)
    if (!undo) return
    await this.driver.batch(undo.statements)
    await this.refreshState()
    this.emitTables(undo.tables)
  }

  /** The ops of a rejected tx, for a guided fix. */
  async failedOps(txId: string): Promise<SyncOp[] | null> {
    const rows = await this.driver.all<{ ops: string }>(
      `SELECT ops FROM _outbox WHERE tx_id = ? AND status = 'failed'`,
      [txId],
    )
    return rows[0] ? (JSON.parse(rows[0].ops) as SyncOp[]) : null
  }

  /**
   * Replaces a rejected tx: its rows go back to the server's version and the
   * replacement ops are committed, in one batch (all or nothing).
   */
  async resolve(txId: string, ops: SyncOp[]): Promise<CommitResult> {
    const undo = await this.discardStatements(txId)
    if (!undo) throw new CoreError('not_found', 'this change is no longer waiting')
    const stamped = await this.prepare(ops)
    const result = await this.write(stamped, undo.statements)
    await this.refreshState()
    this.emitTables(undo.tables)
    return result
  }

  private async discardStatements(
    txId: string,
  ): Promise<{ statements: SqlStatement[]; tables: TableName[] } | null> {
    const ops = await this.failedOps(txId)
    if (!ops) return null

    // Rows still covered by another unsynced tx keep their local version.
    const statements: SqlStatement[] = [
      { sql: 'DELETE FROM _outbox WHERE tx_id = ?', params: [txId] },
      { sql: 'DELETE FROM _pending_rows WHERE tx_id = ?', params: [txId] },
    ]
    const idsByTable = new Map<TableName, string[]>()
    for (const op of ops) {
      if (!isTableName(op.table)) continue
      const stillPending = await this.driver.all(
        'SELECT 1 FROM _pending_rows WHERE tbl = ? AND row_id = ? AND tx_id <> ? LIMIT 1',
        [op.table, String(op.row.id), txId],
      )
      if (stillPending.length === 0)
        idsByTable.set(op.table, [...(idsByTable.get(op.table) ?? []), String(op.row.id)])
    }
    for (const [table, ids] of idsByTable) {
      const serverRows = await this.transport.fetchRows(table, ids)
      const found = new Set<string>()
      for (const row of serverRows) {
        const parsed = rowSchema(table).parse(row)
        found.add(String(parsed.id))
        statements.push(directUpsert(table, parsed))
      }
      const missing = ids.filter((id) => !found.has(id))
      if (missing.length > 0) {
        statements.push({
          sql: `DELETE FROM ${table} WHERE id IN (${placeholders(missing)})`,
          params: missing,
        })
      }
    }
    return { statements, tables: [...idsByTable.keys()] }
  }

  // ---------------------------------------------------------------------------
  // Counters
  // ---------------------------------------------------------------------------

  /** Registers (or fetches) this installation's billing counter and stores it locally. */
  async registerCounter(name?: string): Promise<void> {
    const registered = await this.transport.registerCounter(
      this.tenantId,
      this.installationId,
      name,
    )
    const statements = [
      directUpsert('billing_counters', rowSchema('billing_counters').parse(registered.counter)),
    ]
    for (const seq of registered.sequences) {
      statements.push(directUpsert('counter_sequences', rowSchema('counter_sequences').parse(seq)))
    }
    await this.driver.batch(statements)
    this.emitTables(['billing_counters', 'counter_sequences'])
  }

  // ---------------------------------------------------------------------------
  // Scheduling
  // ---------------------------------------------------------------------------

  /** Starts periodic sync and Realtime-triggered pulls, and syncs once now. */
  start(): void {
    if (this.started) return
    this.started = true
    this.unsubscribeRealtime = this.transport.subscribe(this.tenantId, () => this.trigger())
    this.schedule(0)
  }

  stop(): void {
    this.started = false
    if (this.timer !== null) this.clearTimer(this.timer)
    this.timer = null
    this.unsubscribeRealtime?.()
    this.unsubscribeRealtime = null
  }

  /** Something happened (reconnect, focus, Realtime): sync soon. */
  trigger(): void {
    if (this.started) this.schedule(0)
  }

  private schedule(ms: number): void {
    if (this.timer !== null) this.clearTimer(this.timer)
    this.timer = this.setTimer(() => {
      this.timer = null
      void this.syncNow().finally(() => {
        if (this.started && this.timer === null)
          this.schedule(this.backoffMs || this.pollIntervalMs)
      })
    }, ms)
  }

  /** Wait for any in-flight sync (tests, logout). */
  async idle(): Promise<void> {
    await this.running
  }

  /** Unsynced txs (pending or failed): logging out would lose them. */
  async unsyncedCount(): Promise<number> {
    const rows = await this.driver.all<{ n: number }>('SELECT COUNT(*) AS n FROM _outbox')
    return rows[0]?.n ?? 0
  }
}

/** Upsert a pulled row unless an unsynced local tx touched it. */
function pullUpsert(table: TableName, row: Record<string, unknown>): SqlStatement {
  const columns = Object.keys(tablesByName[table].columns)
  const values = encodeRow(table, row)
  const updates = columns.filter((c) => c !== 'id').map((c) => `${c} = excluded.${c}`)
  return {
    sql: `INSERT INTO ${table} (${columns.join(', ')})
          SELECT ${columns.map(() => '?').join(', ')}
          WHERE NOT EXISTS (SELECT 1 FROM _pending_rows WHERE tbl = ? AND row_id = ?)
          ON CONFLICT (id) DO UPDATE SET ${updates.join(', ')}`,
    params: [...columns.map((c) => values[c] ?? null), table, String(row.id)],
  }
}

function directUpsert(table: TableName, row: Record<string, unknown>): SqlStatement {
  return opStatements({ table, op: 'upsert', row }, '')[0]!
}
